import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Upload, Trash2, Download, PackageCheck, Link2, Hash } from "lucide-react";
import { PageHead } from "../components/PageHead";
import { DataTable } from "../components/DataTable";
import { EquipmentModal } from "../components/EquipmentModal";
import { JobDetailModal } from "../components/JobDetailModal";
import { DisciplineBadge, PortalBadge } from "../components/Badges";
import api, { apiErr } from "../lib/api";
import { fmtDate, fmtNum } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const TABS = [
  ["all", "All"], ["active", "Active"], ["in_lab", "In lab"],
  ["due_soon", "Due soon"], ["overdue", "Overdue"], ["out_of_service", "Unserviceable"],
];
const DISCIPLINES = ["Dimensional", "Pressure", "Torque", "Temperature", "Electronics", "Hydraulics", "Mass", "Flow"];

export default function Equipment() {
  const { user, can } = useAuth();
  const canEdit = can("equipment", "edit");
  const canDelete = user?.role === "manager" || user?.role === "admin";
  // Last cal./Next due/Last delivery note directly drive due/overdue reports
  // and certificate history — inline-editable on this register only for
  // Manager/Admin (mirrors the same manager-only gate enforced server-side).
  const isManager = user?.role === "manager" || user?.role === "admin";
  const [sp] = useSearchParams();
  const [tab, setTab] = useState("all");
  const [stats, setStats] = useState({});
  const [sel, setSel] = useState(null);
  const [openJobId, setOpenJobId] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [busyBulk, setBusyBulk] = useState(false);
  const [closeDn, setCloseDn] = useState("");
  const [closingDn, setClosingDn] = useState(false);
  const [linkDn, setLinkDn] = useState("");
  const [linkingDn, setLinkingDn] = useState(false);
  const [custFilter, setCustFilter] = useState("");
  const [lastIssue, setLastIssue] = useState(null); // { items: [...], considered } | null

  const loadStats = () => api.get("/equipment/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  // SPT-PTE-2026-0057: when a "Customer" column filter is active, surface the
  // highest issue number already allocated for that customer, no paging needed.
  useEffect(() => {
    if (!custFilter.trim()) { setLastIssue(null); return; }
    let cancelled = false;
    api.get("/equipment/last-issue-number", { params: { customer_name_q: custFilter } })
      .then((r) => { if (!cancelled) setLastIssue(r.data); })
      .catch(() => { if (!cancelled) setLastIssue(null); });
    return () => { cancelled = true; };
  }, [custFilter]);

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (tab !== "all") params.set("status_filter", tab);
      const search = sp.get("search");
      if (search) params.set("search", search);
      const res = await api.get(`/equipment/export?${params.toString()}`, {
        responseType: "blob",
        timeout: 600000, // 10 min for very large exports
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `equipment-register-${tab === "all" ? "all" : tab}-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export ready — check your downloads");
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setExporting(false); }
  };

  const bulkDelete = async (ids, clearSelection) => {
    if (!ids.length) return;
    const confirm = window.confirm(
      `Delete ${ids.length} equipment record${ids.length === 1 ? "" : "s"}?\n\n` +
      `This cascades: linked calibration events, certificates and OPEN job-line items will also be removed. ` +
      `Closed / dispatched / paid job-lines are preserved for history. This cannot be undone.`
    );
    if (!confirm) return;
    setBusyBulk(true);
    try {
      const r = await api.post("/equipment/bulk-delete", { ids });
      const c = r.data;
      toast.success(
        `${c.equipment} equipment deleted` +
        ` · ${c.calibration_events} event${c.calibration_events === 1 ? "" : "s"}` +
        ` · ${c.certificates} cert${c.certificates === 1 ? "" : "s"}` +
        ` · ${c.job_items} open job-line${c.job_items === 1 ? "" : "s"}`
      );
      clearSelection();
      setRefresh((x) => x + 1);
      loadStats();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusyBulk(false); }
  };

  const closeByDn = async () => {
    const dn = closeDn.trim();
    if (!dn) return;
    setClosingDn(true);
    try {
      const preview = await api.get("/equipment", { params: { delivery_note_number_q: dn, limit: 1 } });
      const total = preview.data?.total ?? 0;
      if (total === 0) { toast.error(`No equipment found with DN "${dn}"`); return; }
      const confirmed = window.confirm(
        `Close out all ${total} equipment record${total === 1 ? "" : "s"} under DN "${dn}"?\n\n` +
        `This clears their In Lab / dispatch status so each falls back to its normal ` +
        `Active at client / Due soon / Overdue state (based on its calibration due date) — ` +
        `it will NOT show a literal "Closed" badge. Cannot be undone.`
      );
      if (!confirmed) return;
      const r = await api.post("/equipment/close-by-dn", { delivery_note_number: dn });
      toast.success(`Closed ${r.data.updated} equipment record${r.data.updated === 1 ? "" : "s"} under DN "${dn}"`);
      setCloseDn("");
      setRefresh((x) => x + 1);
      loadStats();
    } catch (e) { toast.error(apiErr(e)); } finally { setClosingDn(false); }
  };

  const linkDnToDispatch = async () => {
    const dn = linkDn.trim();
    if (!dn) return;
    setLinkingDn(true);
    try {
      const preview = await api.post("/migrate/link-dn-to-dispatch?apply=false", { delivery_note_number: dn });
      const p = preview.data;
      if (!p.found_equipment) { toast.error(`No equipment found with DN "${dn}"`); return; }
      if (!p.to_link) { toast.info(`Every equipment record under DN "${dn}" is already linked to a Dispatch job.`); return; }
      const customers = p.jobs.map((j) => `${j.customer_name} (${j.item_count} item${j.item_count === 1 ? "" : "s"})`).join(", ");
      const confirmed = window.confirm(
        `DN "${dn}" has ${p.to_link} equipment record${p.to_link === 1 ? "" : "s"} with no Dispatch job ` +
        `(this is why it doesn't show up under Dispatch).\n\n` +
        `Create the missing Dispatch job now for: ${customers}?\n\n` +
        `Quote/PO/Invoice numbers already on the equipment record will be carried over, and each item ` +
        `lands in "Closed" or "Updating" depending on whether it was already delivered.`
      );
      if (!confirmed) return;
      const r = await api.post("/migrate/link-dn-to-dispatch?apply=true", { delivery_note_number: dn });
      const created = r.data.jobs || [];
      toast.success(`Linked ${r.data.to_link} item(s) under DN "${dn}" to Dispatch`);
      setLinkDn("");
      if (created.length && created[0].job_id) setOpenJobId(created[0].job_id);
    } catch (e) { toast.error(apiErr(e)); } finally { setLinkingDn(false); }
  };

  const columns = [
    { key: "description", label: "Equipment", sortable: true, editable: true, colSearch: true, render: (v, row) => (<span className="cell-2" style={{ display: "inline-flex" }}><span className="t-strong">{v || "—"}</span><span className="s">{row.manufacturer || ""}</span></span>) },
    { key: "serial_number", label: "Serial", sortable: true, editable: true, colSearch: true, mono: true, render: (v) => v ? v : <span style={{ color: "var(--red)", fontWeight: 600 }}>⚠ none</span> },
    { key: "part_number", label: "Part #", sortable: true, editable: true, colSearch: true, mono: true, render: (v) => v || <span className="t-mut">—</span> },
    { key: "issue_number", label: "Issue #", sortable: true, editable: true, colSearch: true, mono: true, render: (v) => v || <span className="t-mut">—</span> },
    { key: "discipline", label: "Discipline", sortable: true, editable: true, colSearch: true, type: "select", options: DISCIPLINES, render: (v) => <DisciplineBadge name={v} /> },
    { key: "customer_name", label: "Customer", sortable: true, colSearch: true, render: (v) => <span className="t-mut">{v || "—"}</span> },
    { key: "calibration_period_days", label: "Period", sortable: true, editable: true, type: "number", mono: true, align: "right" },
    { key: "last_calibration_date", label: "Last cal.", sortable: true, editable: isManager, type: "date", mono: true, render: (v) => <span className="t-mut">{fmtDate(v)}</span> },
    { key: "calibration_due_date", label: "Next due", sortable: true, editable: isManager, type: "date", mono: true, render: (v, row) => row.scrapped ? <span style={{ color: "var(--text-3)", textDecoration: "line-through" }}>—</span> : <span style={{ fontWeight: 700 }}>{fmtDate(v)}</span> },
    { key: "delivery_note_number", label: "Current DN", sortable: true, editable: isManager, colSearch: true, mono: true, render: (v) => v || <span className="t-mut">—</span> },
    { key: "portal_status", label: "Status", render: (v) => <PortalBadge status={v} labelOverrides={{ active: ["Active at client", "b-green"] }} /> },
  ];

  return (
    <>
      <PageHead crumb="Records" title="Equipment Register"
        sub="One consolidated register — sortable, searchable, and editable inline. Click a row for full history."
        actions={
          <>
            <button className="btn ghost" onClick={exportCsv} disabled={exporting} data-testid="equip-export-csv">
              <Download size={15} /> {exporting ? "Exporting…" : "Export CSV"}
            </button>
            {canEdit && can("import", "view") && <a className="btn ghost" href="/app/import"><Upload size={15} /> Import (Caspio)</a>}
          </>
        } />

      {custFilter.trim() && lastIssue && lastIssue.items.length > 0 && (
        <div className="card pad" data-testid="equip-last-issue-number-box"
          style={{ marginBottom: 10, background: "var(--card-2)", borderLeft: "3px solid var(--gold-deep)" }}>
          <div className="t-strong" style={{ fontSize: 12.5, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <Hash size={13} color="var(--gold-deep)" /> Last issue # for "{custFilter}": {" "}
            <span className="mono" style={{ color: "var(--gold-deep)" }}>{lastIssue.items[0]}</span>
            <span className="t-mut" style={{ fontWeight: 400 }}>({fmtNum(lastIssue.considered)} numbered records considered)</span>
          </div>
          {lastIssue.items.length > 1 && (
            <div className="t-mut" style={{ fontSize: 12 }} data-testid="equip-last-issue-number-more">
              Also recently used: {lastIssue.items.slice(1).join(", ")}
            </div>
          )}
        </div>
      )}
      {custFilter.trim() && lastIssue && lastIssue.items.length === 0 && (
        <div className="card pad" data-testid="equip-last-issue-number-empty"
          style={{ marginBottom: 10, background: "var(--card-2)" }}>
          <span className="t-mut" style={{ fontSize: 12.5 }}>No issue numbers on record for "{custFilter}".</span>
        </div>
      )}

      <DataTable
        endpoint="/equipment"
        params={{ status_filter: tab === "all" ? undefined : tab, search: sp.get("search") || undefined }}
        columns={columns}
        rowKey="id"
        testid="equip"
        canEdit={canEdit}
        editEndpoint={(row) => `/equipment/${row.id}`}
        defaultSort={{ by: "updated_at", dir: "desc" }}
        searchPlaceholder="Search description, serial, asset, DN, manufacturer…"
        onRowClick={(row) => setSel(row.id)}
        onChanged={loadStats}
        refreshSignal={refresh}
        onColSearchChange={(cs) => setCustFilter(cs.customer_name || "")}
        selectable={canDelete}
        bulkToolbar={canDelete ? ((ids, clear) => (
          <button className="btn sm" disabled={busyBulk}
            onClick={() => bulkDelete(ids, clear)}
            data-testid="equip-bulk-delete"
            style={{ background: "#a32424", color: "#fff", borderColor: "#a32424" }}>
            <Trash2 size={13} /> {busyBulk ? "Deleting…" : `Delete ${ids.length} selected`}
          </button>
        )) : undefined}
        toolbar={TABS.map(([k, label]) => (
          <button key={k} className={`ftab ${tab === k ? "active" : ""}`} data-testid={`equip-tab-${k}`} onClick={() => setTab(k)}>
            {label}{stats[k] != null && <span className="c">{fmtNum(stats[k])}</span>}
          </button>
        ))}
        rightToolbar={
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input className="input" style={{ width: 150, padding: "7px 10px", fontSize: 13 }} placeholder="Close out by DN…"
              value={closeDn} onChange={(e) => setCloseDn(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") closeByDn(); }}
              data-testid="equip-close-by-dn-input" />
            <button className="btn sm" disabled={closingDn || !closeDn.trim()} onClick={closeByDn}
              data-testid="equip-close-by-dn-btn">
              <PackageCheck size={13} /> {closingDn ? "Closing…" : "Close out DN"}
            </button>
            {canEdit && (
              <>
                <input className="input" style={{ width: 150, padding: "7px 10px", fontSize: 13 }} placeholder="Link DN to Dispatch…"
                  value={linkDn} onChange={(e) => setLinkDn(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") linkDnToDispatch(); }}
                  title="For Caspio-origin DNs that have no Dispatch job yet — creates one so it can be closed out with Quote/PO/Invoice numbers."
                  data-testid="equip-link-dn-input" />
                <button className="btn sm" disabled={linkingDn || !linkDn.trim()} onClick={linkDnToDispatch}
                  data-testid="equip-link-dn-btn">
                  <Link2 size={13} /> {linkingDn ? "Linking…" : "Link to Dispatch"}
                </button>
              </>
            )}
          </div>
        }
      />

      {sel && (
        <EquipmentModal id={sel} canEdit={canEdit} onClose={() => setSel(null)}
          onChanged={() => { loadStats(); setRefresh((x) => x + 1); }}
          onOpenJob={(jobId) => { setSel(null); setOpenJobId(jobId); }} />
      )}
      {openJobId && (
        <JobDetailModal jobId={openJobId} onClose={() => setOpenJobId(null)}
          onChanged={() => { loadStats(); setRefresh((x) => x + 1); }} />
      )}
    </>
  );
}
